import React, { useState, useEffect } from 'react';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, AreaChart, Area
} from 'recharts';
import Header from '../../components/ui/Header';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';

const DEFAULT_REPORT_TEMPLATES = [
  { id: 'rpt-1', name: 'Monthly Technician Performance', description: 'Ticket completion rates, avg resolution time per technician', category: 'Performance', icon: 'UserCog', color: 'blue' },
  { id: 'rpt-2', name: 'Ticket Status Summary', description: 'Overview of open, in-progress, and closed tickets', category: 'Operations', icon: 'ClipboardList', color: 'green' },
  { id: 'rpt-3', name: 'Invoice & Revenue Report', description: 'Paid, pending, and overdue invoices with revenue totals', category: 'Finance', icon: 'DollarSign', color: 'yellow' },
  { id: 'rpt-4', name: 'Inventory Usage Analytics', description: 'Stock levels, reorder alerts, and usage trends', category: 'Inventory', icon: 'Warehouse', color: 'purple' },
  { id: 'rpt-5', name: 'Customer Support Overview', description: 'Support request volume, resolution times, satisfaction', category: 'Support', icon: 'Headphones', color: 'orange' },
  { id: 'rpt-6', name: 'Quote Conversion Rate', description: 'Quotes sent vs accepted, average deal value', category: 'Sales', icon: 'FileText', color: 'teal' }
];

const CATEGORY_COLORS = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  yellow: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  teal: 'bg-teal-100 text-teal-700 border-teal-200'
};

const CHART_COLORS = ['#6366f1', '#22c55e', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'];

const STORAGE_KEY = 'report_saved_templates';

const MOCK_REVENUE_DATA = [
  { month: 'Sep', revenue: 42000, invoices: 38, collected: 36000 },
  { month: 'Oct', revenue: 51000, invoices: 45, collected: 48000 },
  { month: 'Nov', revenue: 47000, invoices: 41, collected: 44000 },
  { month: 'Dec', revenue: 63000, invoices: 58, collected: 59000 },
  { month: 'Jan', revenue: 55000, invoices: 50, collected: 52000 },
  { month: 'Feb', revenue: 71000, invoices: 64, collected: 68000 },
  { month: 'Mar', revenue: 68000, invoices: 61, collected: 65000 },
];

const MOCK_TICKET_DATA = [
  { day: 'Mon', opened: 24, resolved: 20, pending: 4 },
  { day: 'Tue', opened: 31, resolved: 28, pending: 7 },
  { day: 'Wed', opened: 18, resolved: 17, pending: 2 },
  { day: 'Thu', opened: 27, resolved: 22, pending: 9 },
  { day: 'Fri', opened: 35, resolved: 30, pending: 5 },
  { day: 'Sat', opened: 12, resolved: 11, pending: 1 },
  { day: 'Sun', opened: 8, resolved: 8, pending: 0 },
];

const MOCK_TECHNICIAN_DATA = [
  { name: 'Alex M.', completed: 48, avgTime: 3.2, satisfaction: 4.8, revenue: 12400 },
  { name: 'Jordan K.', completed: 42, avgTime: 4.1, satisfaction: 4.6, revenue: 10800 },
  { name: 'Sam R.', completed: 55, avgTime: 2.9, satisfaction: 4.9, revenue: 14200 },
  { name: 'Taylor B.', completed: 38, avgTime: 5.0, satisfaction: 4.3, revenue: 9600 },
  { name: 'Casey L.', completed: 61, avgTime: 2.7, satisfaction: 4.7, revenue: 15800 },
];

const MOCK_CUSTOMER_DATA = [
  { name: 'New', value: 28, color: '#6366f1' },
  { name: 'Returning', value: 54, color: '#22c55e' },
  { name: 'At Risk', value: 12, color: '#f59e0b' },
  { name: 'Churned', value: 6, color: '#ef4444' },
];

const MOCK_RESOLUTION_DATA = [
  { range: '< 1hr', count: 45 },
  { range: '1-4hrs', count: 82 },
  { range: '4-8hrs', count: 56 },
  { range: '1-2days', count: 34 },
  { range: '2-5days', count: 18 },
  { range: '5+ days', count: 7 },
];

const Reports = () => {
  const { orgMembership } = useAuth();
  const [savedTemplates, setSavedTemplates] = useState(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  });
  const [isSaving, setIsSaving] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState('');
  const [newTemplateDesc, setNewTemplateDesc] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [dateRange, setDateRange] = useState('7d');
  const [analyticsLoading, setAnalyticsLoading] = useState(false);
  const [revenueData, setRevenueData] = useState(MOCK_REVENUE_DATA);
  const [ticketData, setTicketData] = useState(MOCK_TICKET_DATA);
  const [technicianData, setTechnicianData] = useState(MOCK_TECHNICIAN_DATA);
  const [kpiMetrics, setKpiMetrics] = useState({
    totalRevenue: 397000,
    revenueChange: '+14.2%',
    ticketsClosed: 1284,
    ticketsChange: '+12%',
    avgResolution: '4.2 hrs',
    resolutionChange: '-8%',
    customerSatisfaction: '4.7',
    satisfactionChange: '+0.3',
  });

  const orgId = orgMembership?.organization_id;

  const categories = ['All', 'Performance', 'Operations', 'Finance', 'Inventory', 'Support', 'Sales'];
  const filteredTemplates = DEFAULT_REPORT_TEMPLATES?.filter(
    t => selectedCategory === 'All' || t?.category === selectedCategory
  );

  const showSuccess = (msg) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const fetchAnalytics = async () => {
    if (!orgId) return;
    setAnalyticsLoading(true);
    try {
      // Fetch real ticket data
      const { data: tickets } = await supabase?.from('tickets')?.select('id, status, created_at, resolved_at, assigned_to')?.eq('organization_id', orgId)?.order('created_at', { ascending: false })?.limit(500);

      // Fetch real invoice data
      const { data: invoices } = await supabase?.from('invoices')?.select('id, total_amount, status, created_at, paid_at')?.eq('organization_id', orgId)?.order('created_at', { ascending: false })?.limit(500);

      if (tickets?.length > 0) {
        const closed = tickets?.filter(t => t?.status === 'closed' || t?.status === 'resolved')?.length;
        setKpiMetrics(prev => ({ ...prev, ticketsClosed: closed || prev?.ticketsClosed }));
      }

      if (invoices?.length > 0) {
        const totalRev = invoices?.filter(i => i?.status === 'paid')?.reduce((sum, i) => sum + (i?.total_amount || 0), 0);
        if (totalRev > 0) {
          setKpiMetrics(prev => ({ ...prev, totalRevenue: totalRev }));
        }
      }
    } catch (err) {
      console.error('Analytics fetch error:', err);
    } finally {
      setAnalyticsLoading(false);
    }
  };

  useEffect(() => {
    fetchAnalytics();
  }, [orgId, dateRange]);

  const handleSaveTemplate = () => {
    if (!newTemplateName?.trim()) return;
    const newTpl = {
      id: `custom-${Date.now()}`,
      name: newTemplateName?.trim(),
      description: newTemplateDesc?.trim(),
      category: 'Custom',
      icon: 'FileBarChart',
      color: 'blue',
      createdAt: new Date()?.toISOString()
    };
    const updated = [...savedTemplates, newTpl];
    setSavedTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setNewTemplateName('');
    setNewTemplateDesc('');
    setIsSaving(false);
    showSuccess(`Template "${newTpl?.name}" saved!`);
  };

  const handleDeleteTemplate = (id) => {
    const updated = savedTemplates?.filter(t => t?.id !== id);
    setSavedTemplates(updated);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    setDeleteConfirm(null);
    showSuccess('Template deleted.');
  };

  const handleRunReport = (template) => {
    showSuccess(`Running "${template?.name}"...`);
  };

  const exportCSV = (data, filename) => {
    if (!data?.length) return;
    const headers = Object.keys(data?.[0]);
    const csvContent = [
      headers?.join(','),
      ...data?.map(row => headers?.map(h => `"${row?.[h] ?? ''}"`)?.join(','))
    ]?.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    a?.click();
    URL.revokeObjectURL(url);
    showSuccess(`Exported ${filename}.csv`);
  };

  const formatCurrency = (val) => {
    if (val >= 1000000) return `$${(val / 1000000)?.toFixed(1)}M`;
    if (val >= 1000) return `$${(val / 1000)?.toFixed(0)}K`;
    return `$${val}`;
  };

  const TABS = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'revenue', label: 'Revenue', icon: 'TrendingUp' },
    { id: 'tickets', label: 'Tickets', icon: 'ClipboardList' },
    { id: 'technicians', label: 'Technicians', icon: 'UserCog' },
    { id: 'customers', label: 'Customers', icon: 'Users' },
    { id: 'templates', label: 'Templates', icon: 'LayoutTemplate' },
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <main className="pt-6 pb-8 px-4 lg:px-6">
        {/* Page Header */}
        <div className="mb-6 flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Reports & Analytics</h1>
            <p className="text-sm text-muted-foreground mt-1">Advanced analytics, performance metrics, and exportable reports</p>
          </div>
          <div className="flex items-center gap-3">
            {successMsg && (
              <div className="flex items-center gap-2 px-4 py-2 bg-success/10 border border-success/20 text-success text-sm font-medium rounded-lg">
                <Icon name="CheckCircle" size={14} />
                {successMsg}
              </div>
            )}
            {/* Date Range Filter */}
            <div className="flex items-center gap-1 bg-muted/40 p-1 rounded-lg">
              {['7d', '30d', '90d', '1y']?.map(range => (
                <button
                  key={range}
                  onClick={() => setDateRange(range)}
                  className={`px-3 py-1 text-xs font-medium rounded-md transition-colors ${
                    dateRange === range ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {range}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          {[
            { label: 'Total Revenue', value: formatCurrency(kpiMetrics?.totalRevenue), change: kpiMetrics?.revenueChange, icon: 'DollarSign', trend: 'up' },
            { label: 'Tickets Closed', value: kpiMetrics?.ticketsClosed?.toLocaleString(), change: kpiMetrics?.ticketsChange, icon: 'CheckCircle', trend: 'up' },
            { label: 'Avg Resolution', value: kpiMetrics?.avgResolution, change: kpiMetrics?.resolutionChange, icon: 'Clock', trend: 'up' },
            { label: 'Satisfaction', value: `${kpiMetrics?.customerSatisfaction}/5`, change: kpiMetrics?.satisfactionChange, icon: 'Star', trend: 'up' },
          ]?.map((kpi, idx) => (
            <div key={idx} className="bg-card border border-border rounded-xl p-4 shadow-elevation-1">
              <div className="flex items-center justify-between mb-3">
                <div className="w-9 h-9 bg-primary/10 rounded-lg flex items-center justify-center">
                  <Icon name={kpi?.icon} size={16} className="text-primary" />
                </div>
                <span className="text-xs font-medium px-2 py-0.5 bg-success/10 text-success rounded-full">{kpi?.change}</span>
              </div>
              <p className="text-xl font-bold text-foreground">{kpi?.value}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{kpi?.label}</p>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-5 bg-muted/40 p-1 rounded-lg overflow-x-auto">
          {TABS?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-md transition-colors whitespace-nowrap ${
                activeTab === tab?.id ? 'bg-card text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              <Icon name={tab?.icon} size={13} />
              {tab?.label}
            </button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Trend */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Revenue Trend</h3>
                </div>
                <Button variant="ghost" size="xs" onClick={() => exportCSV(revenueData, 'revenue_trend')} iconName="Download" iconPosition="left">Export</Button>
              </div>
              <div className="p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <AreaChart data={revenueData}>
                    <defs>
                      <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} tickFormatter={v => `$${v/1000}K`} />
                    <Tooltip formatter={(v) => [`$${v?.toLocaleString()}`, 'Revenue']} />
                    <Area type="monotone" dataKey="revenue" stroke="#6366f1" fill="url(#revGrad)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Ticket Resolution */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="ClipboardList" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Ticket Resolution Time</h3>
                </div>
                <Button variant="ghost" size="xs" onClick={() => exportCSV(MOCK_RESOLUTION_DATA, 'resolution_times')} iconName="Download" iconPosition="left">Export</Button>
              </div>
              <div className="p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={MOCK_RESOLUTION_DATA}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="range" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Bar dataKey="count" fill="#22c55e" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Customer Breakdown */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="Users" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Customer Analytics</h3>
                </div>
              </div>
              <div className="p-4 flex items-center gap-6">
                <ResponsiveContainer width={160} height={160}>
                  <PieChart>
                    <Pie data={MOCK_CUSTOMER_DATA} cx="50%" cy="50%" innerRadius={45} outerRadius={70} dataKey="value">
                      {MOCK_CUSTOMER_DATA?.map((entry, idx) => (
                        <Cell key={idx} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
                <div className="flex-1 space-y-2">
                  {MOCK_CUSTOMER_DATA?.map((item, idx) => (
                    <div key={idx} className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <div className="w-3 h-3 rounded-full" style={{ backgroundColor: item?.color }} />
                        <span className="text-xs text-muted-foreground">{item?.name}</span>
                      </div>
                      <span className="text-xs font-semibold text-foreground">{item?.value}%</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Weekly Tickets */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="BarChart3" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Weekly Ticket Activity</h3>
                </div>
                <Button variant="ghost" size="xs" onClick={() => exportCSV(ticketData, 'weekly_tickets')} iconName="Download" iconPosition="left">Export</Button>
              </div>
              <div className="p-4">
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={ticketData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="opened" fill="#6366f1" radius={[3, 3, 0, 0]} name="Opened" />
                    <Bar dataKey="resolved" fill="#22c55e" radius={[3, 3, 0, 0]} name="Resolved" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
          </div>
        )}

        {/* Revenue Tab */}
        {activeTab === 'revenue' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="TrendingUp" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Revenue & Collections Trend</h3>
                </div>
                <Button variant="outline" size="sm" onClick={() => exportCSV(revenueData, 'revenue_report')} iconName="Download" iconPosition="left">Export CSV</Button>
              </div>
              <div className="p-5">
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={revenueData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} tickFormatter={v => `$${v/1000}K`} />
                    <Tooltip formatter={(v) => [`$${v?.toLocaleString()}`]} />
                    <Legend />
                    <Line type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={2.5} dot={{ r: 4 }} name="Revenue" />
                    <Line type="monotone" dataKey="collected" stroke="#22c55e" strokeWidth={2.5} dot={{ r: 4 }} name="Collected" />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Icon name="Table" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Monthly Revenue Breakdown</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {['Month', 'Revenue', 'Invoices', 'Collected', 'Collection Rate']?.map(h => (
                        <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {revenueData?.map((row, idx) => (
                      <tr key={idx} className="hover:bg-muted/20 transition-colors">
                        <td className="px-5 py-3 font-medium text-foreground">{row?.month}</td>
                        <td className="px-5 py-3 text-foreground">${row?.revenue?.toLocaleString()}</td>
                        <td className="px-5 py-3 text-foreground">{row?.invoices}</td>
                        <td className="px-5 py-3 text-success">${row?.collected?.toLocaleString()}</td>
                        <td className="px-5 py-3">
                          <span className="text-xs font-medium px-2 py-0.5 bg-success/10 text-success rounded-full">
                            {Math.round((row?.collected / row?.revenue) * 100)}%
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Tickets Tab */}
        {activeTab === 'tickets' && (
          <div className="space-y-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card border border-border rounded-xl shadow-elevation-1">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Icon name="BarChart3" size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Daily Ticket Volume</h3>
                  </div>
                  <Button variant="ghost" size="xs" onClick={() => exportCSV(ticketData, 'ticket_volume')} iconName="Download" iconPosition="left">Export</Button>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={ticketData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 11 }} />
                      <Tooltip />
                      <Legend wrapperStyle={{ fontSize: 11 }} />
                      <Bar dataKey="opened" fill="#6366f1" radius={[3, 3, 0, 0]} name="Opened" />
                      <Bar dataKey="resolved" fill="#22c55e" radius={[3, 3, 0, 0]} name="Resolved" />
                      <Bar dataKey="pending" fill="#f59e0b" radius={[3, 3, 0, 0]} name="Pending" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="bg-card border border-border rounded-xl shadow-elevation-1">
                <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                  <div className="flex items-center gap-2">
                    <Icon name="Clock" size={16} className="text-primary" />
                    <h3 className="text-sm font-semibold text-foreground">Resolution Time Distribution</h3>
                  </div>
                  <Button variant="ghost" size="xs" onClick={() => exportCSV(MOCK_RESOLUTION_DATA, 'resolution_dist')} iconName="Download" iconPosition="left">Export</Button>
                </div>
                <div className="p-4">
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={MOCK_RESOLUTION_DATA} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis type="number" tick={{ fontSize: 11 }} />
                      <YAxis dataKey="range" type="category" tick={{ fontSize: 11 }} width={60} />
                      <Tooltip />
                      <Bar dataKey="count" fill="#8b5cf6" radius={[0, 4, 4, 0]} name="Tickets" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Technicians Tab */}
        {activeTab === 'technicians' && (
          <div className="space-y-6">
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="UserCog" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Technician Performance Metrics</h3>
                </div>
                <Button variant="outline" size="sm" onClick={() => exportCSV(technicianData, 'technician_performance')} iconName="Download" iconPosition="left">Export CSV</Button>
              </div>
              <div className="p-5">
                <ResponsiveContainer width="100%" height={240}>
                  <BarChart data={technicianData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                    <YAxis tick={{ fontSize: 11 }} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    <Bar dataKey="completed" fill="#6366f1" radius={[4, 4, 0, 0]} name="Tickets Completed" />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Icon name="Table" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Detailed Performance Table</h3>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border bg-muted/30">
                      {['Technician', 'Tickets Completed', 'Avg Resolution (hrs)', 'Satisfaction', 'Revenue Generated']?.map(h => (
                        <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-muted-foreground">{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {technicianData?.map((tech, idx) => (
                      <tr key={idx} className="hover:bg-muted/20 transition-colors">
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ backgroundColor: CHART_COLORS?.[idx % CHART_COLORS?.length] }}>
                              {tech?.name?.charAt(0)}
                            </div>
                            <span className="font-medium text-foreground">{tech?.name}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-foreground">{tech?.completed}</td>
                        <td className="px-5 py-3">
                          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                            tech?.avgTime <= 3 ? 'bg-success/10 text-success' :
                            tech?.avgTime <= 4.5 ? 'bg-warning/10 text-warning': 'bg-error/10 text-error'
                          }`}>{tech?.avgTime} hrs</span>
                        </td>
                        <td className="px-5 py-3">
                          <div className="flex items-center gap-1">
                            <Icon name="Star" size={12} className="text-warning" />
                            <span className="text-foreground font-medium">{tech?.satisfaction}</span>
                          </div>
                        </td>
                        <td className="px-5 py-3 text-success font-medium">${tech?.revenue?.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Customers Tab */}
        {activeTab === 'customers' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="PieChart" size={16} className="text-primary" />
                  <h3 className="text-sm font-semibold text-foreground">Customer Segmentation</h3>
                </div>
                <Button variant="ghost" size="xs" onClick={() => exportCSV(MOCK_CUSTOMER_DATA, 'customer_segments')} iconName="Download" iconPosition="left">Export</Button>
              </div>
              <div className="p-5 flex flex-col items-center">
                <ResponsiveContainer width="100%" height={240}>
                  <PieChart>
                    <Pie data={MOCK_CUSTOMER_DATA} cx="50%" cy="50%" outerRadius={90} dataKey="value" label={({ name, value }) => `${name}: ${value}%`} labelLine={false}>
                      {MOCK_CUSTOMER_DATA?.map((entry, idx) => (
                        <Cell key={idx} fill={entry?.color} />
                      ))}
                    </Pie>
                    <Tooltip formatter={(v) => [`${v}%`]} />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </div>
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="px-5 py-4 border-b border-border flex items-center gap-2">
                <Icon name="TrendingUp" size={16} className="text-primary" />
                <h3 className="text-sm font-semibold text-foreground">Customer Metrics</h3>
              </div>
              <div className="p-5 space-y-4">
                {[
                  { label: 'Total Customers', value: '284', icon: 'Users', change: '+18 this month' },
                  { label: 'New This Month', value: '28', icon: 'UserPlus', change: '+12% vs last month' },
                  { label: 'Avg Ticket Value', value: '$312', icon: 'DollarSign', change: '+$24 vs last month' },
                  { label: 'Retention Rate', value: '87%', icon: 'RefreshCw', change: '+2% vs last month' },
                  { label: 'Avg Tickets/Customer', value: '4.5', icon: 'ClipboardList', change: 'Stable' },
                ]?.map((metric, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-muted/30 rounded-lg border border-border">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name={metric?.icon} size={14} className="text-primary" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-foreground truncate">{metric?.value}</p>
                        <p className="text-xs text-muted-foreground">{metric?.label}</p>
                      </div>
                    </div>
                    <span className="text-xs text-success">{metric?.change}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Saved Templates */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="flex items-center justify-between px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2">
                  <Icon name="Bookmark" size={18} className="text-primary" />
                  <h2 className="text-base font-semibold text-foreground">Saved Templates</h2>
                  {savedTemplates?.length > 0 && (
                    <span className="px-2 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">{savedTemplates?.length}</span>
                  )}
                </div>
                <Button variant="outline" size="sm" onClick={() => setIsSaving(prev => !prev)} iconName="Plus" iconPosition="left">New Template</Button>
              </div>
              {isSaving && (
                <div className="px-5 py-4 border-b border-border bg-muted/20">
                  <div className="space-y-3">
                    <Input placeholder="Template name *" value={newTemplateName} onChange={e => setNewTemplateName(e?.target?.value)} autoFocus />
                    <Input placeholder="Description (optional)" value={newTemplateDesc} onChange={e => setNewTemplateDesc(e?.target?.value)} />
                    <div className="flex gap-2">
                      <Button variant="primary" size="sm" onClick={handleSaveTemplate} disabled={!newTemplateName?.trim()} className="flex-1">Save</Button>
                      <Button variant="ghost" size="sm" onClick={() => { setIsSaving(false); setNewTemplateName(''); setNewTemplateDesc(''); }} className="flex-1">Cancel</Button>
                    </div>
                  </div>
                </div>
              )}
              <div className="max-h-80 overflow-y-auto">
                {savedTemplates?.length === 0 ? (
                  <div className="px-5 py-10 text-center">
                    <Icon name="BookmarkX" size={32} className="mx-auto mb-2 opacity-40 text-muted-foreground" />
                    <p className="text-sm text-muted-foreground">No saved templates yet</p>
                  </div>
                ) : (
                  <div className="p-3 space-y-1">
                    {savedTemplates?.map(tpl => (
                      <div key={tpl?.id} className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/60 group transition-colors">
                        <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Icon name={tpl?.icon || 'FileBarChart'} size={16} className="text-primary" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-foreground truncate">{tpl?.name}</p>
                          {tpl?.description && <p className="text-xs text-muted-foreground truncate">{tpl?.description}</p>}
                        </div>
                        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                          <button onClick={() => handleRunReport(tpl)} className="px-2 py-1 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-md transition-colors">Run</button>
                          {deleteConfirm === tpl?.id ? (
                            <>
                              <button onClick={() => handleDeleteTemplate(tpl?.id)} className="px-2 py-1 text-xs font-medium text-error bg-error/10 hover:bg-error/20 rounded-md transition-colors">Confirm</button>
                              <button onClick={() => setDeleteConfirm(null)} className="px-2 py-1 text-xs font-medium text-muted-foreground hover:bg-muted rounded-md transition-colors">Cancel</button>
                            </>
                          ) : (
                            <button onClick={() => setDeleteConfirm(tpl?.id)} className="p-1 text-muted-foreground hover:text-error rounded-md transition-colors">
                              <Icon name="Trash2" size={14} />
                            </button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Report Template Library */}
            <div className="bg-card border border-border rounded-xl shadow-elevation-1">
              <div className="px-5 py-4 border-b border-border">
                <div className="flex items-center gap-2 mb-3">
                  <Icon name="LayoutTemplate" size={18} className="text-primary" />
                  <h2 className="text-base font-semibold text-foreground">Report Templates</h2>
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {categories?.map(cat => (
                    <button key={cat} onClick={() => setSelectedCategory(cat)}
                      className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                        selectedCategory === cat ? 'bg-primary text-primary-foreground border-primary' : 'bg-muted/40 text-muted-foreground border-border hover:border-primary hover:text-primary'
                      }`}>
                      {cat}
                    </button>
                  ))}
                </div>
              </div>
              <div className="max-h-80 overflow-y-auto">
                <div className="p-3 space-y-2">
                  {filteredTemplates?.map(tpl => (
                    <div key={tpl?.id} className="flex items-center gap-3 px-3 py-3 rounded-lg border border-border hover:border-primary/40 hover:bg-muted/30 group transition-all">
                      <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 border ${CATEGORY_COLORS?.[tpl?.color] || CATEGORY_COLORS?.blue}`}>
                        <Icon name={tpl?.icon} size={16} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-medium text-foreground truncate">{tpl?.name}</p>
                          <span className="text-xs px-1.5 py-0.5 bg-muted text-muted-foreground rounded-full flex-shrink-0">{tpl?.category}</span>
                        </div>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{tpl?.description}</p>
                      </div>
                      <button onClick={() => handleRunReport(tpl)} className="flex-shrink-0 flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors opacity-0 group-hover:opacity-100">
                        <Icon name="Play" size={12} />
                        Run
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default Reports;
